import { useState, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetPostsInfinite,
  useCreatePost,
  axiosServiceInstance,
  getMediaUrl,
  getSession,
  useGetAllProfileQuery,
  useGetSpaceTermsAcceptance,
  fetchMentionUsers,
} from '@onesource/services';
import { RichTextEditor, type TiptapDoc } from '@onesource/components';
import type { TSpaceBase, TPostAttachment } from '@onesource/schemas';
import { PostCard } from './post-card';
import { FeedTermsAcceptanceDialog } from './feed-terms-acceptance-dialog';
import { CiImageOn } from 'react-icons/ci';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import DOMPurify from 'dompurify';
import WhosOnline from '#/components/whos-online';

interface UploadingImage {
  file: File;
  preview: string;
  uploading: boolean;
  url?: string;
}

const getPresignedUrl = async (
  key: string,
  contentType: string,
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>('/upload/presigned-url', {
    params: { key, contentType, folder: 'community-hub-posts' },
  });
  return res.data;
};

const uploadImage = async (file: File): Promise<string> => {
  const key = `${Date.now()}-${file.name}`;
  const { uploadUrl, publicUrl } = await getPresignedUrl(key, file.type);

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  return publicUrl || uploadUrl.split('?')[0];
};

interface FeedTabContentProps {
  subSpace: TSpaceBase;
  parentSpace: TSpaceBase;
}

/**
 * Feed Tab Content
 * Displays posts from the feed sub-space
 */
export const FeedTabContent = ({ subSpace, parentSpace }: FeedTabContentProps) => {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetPostsInfinite(subSpace.id);
  const [isExpanded, setIsExpanded] = useState(false);
  const [postBody, setPostBody] = useState<TiptapDoc | null>(null);
  const [images, setImages] = useState<UploadingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const createPostMutation = useCreatePost(subSpace.id);
  const theme = useTheme();
  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const { data: termsAcceptance } = useGetSpaceTermsAcceptance(subSpace.id);
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

  const showTermsDialog = !!(termsAcceptance?.requiresAcceptance && !termsAcceptance.accepted && termsAcceptance.terms);

  // Flatten posts from all pages
  const posts = data?.pages.flatMap(page => page.posts) ?? [];

  const bannerSrc = isBelowMd ? subSpace.mobileBannerImage || subSpace.bannerImage : subSpace.bannerImage;
  const bannerUrl = bannerSrc ? getMediaUrl(bannerSrc) : null;

  // Intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Limit to 10 images
    const remainingSlots = 10 - images.length;
    const filesToUpload = [...files].slice(0, remainingSlots);

    for (const file of filesToUpload) {
      const preview = URL.createObjectURL(file);
      const newImage: UploadingImage = { file, preview, uploading: true };

      setImages(prev => [...prev, newImage]);

      try {
        const url = await uploadImage(file);
        setImages(prev => prev.map(img => (img.preview === preview ? { ...img, uploading: false, url } : img)));
      } catch {
        // Remove failed upload
        setImages(prev => prev.filter(img => img.preview !== preview));
        URL.revokeObjectURL(preview);
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (preview: string) => {
    setImages(prev => {
      const img = prev.find(i => i.preview === preview);
      if (img) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter(i => i.preview !== preview);
    });
  };

  const handlePost = async () => {
    if (!postBody && images.length === 0) {
      return;
    }
    if (images.some(img => img.uploading)) {
      return;
    }

    const attachments: TPostAttachment[] = images
      .filter(img => img.url)
      .map(img => ({
        url: img.url!,
        type: 'image' as const,
      }));

    try {
      await createPostMutation.mutateAsync({
        body: postBody ? JSON.stringify(postBody) : '',
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setPostBody(null);
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);
      setIsExpanded(false);
    } catch {
      // Error handling - mutation will show error state
    }
  };

  const handleCancel = () => {
    setPostBody(null);
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setIsExpanded(false);
  };

  const hasUploadingImages = images.some(img => img.uploading);
  const canPost = (postBody || images.length > 0) && !hasUploadingImages && !createPostMutation.isPending;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load posts: {error.message}</Alert>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          sx={{
            flex: 1,
            height: { xs: '100%', md: 'calc(100vh - 130px)' },
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            maxWidth: { xs: '100%', md: 'calc(100% - 250px)' },
          }}
        >
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              fontFamily: '"Figtree", sans-serif',
              fontSize: 16,
              lineHeight: '22px',
              fontWeight: 400,
              color: theme => theme.palette.grey[600],
              '& .MuiBreadcrumbs-separator': {
                mx: 0.6,
              },
            }}
          >
            <Box component="span" style={{ color: theme.palette.grey[600] }}>
              Community
            </Box>
            <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subSpace.name ?? '') }} />
            </Typography>
          </Breadcrumbs>

          {/* Banner Image */}
          {bannerUrl ? (
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 5 },
                width: '100%',
                height: { xs: 106, md: 146 },
                borderRadius: '11px',
                objectFit: 'cover',
                backgroundImage: `url(${bannerUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              {' '}
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '22px', md: '30px' },
                  lineHeight: { xs: '23px', md: '32px' },
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  '& p': {
                    margin: '0px',
                    '& span': { color: `${theme.palette.common.white} !important` },
                  },
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(subSpace.description ?? '') }} />
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 279,
                borderRadius: '15px',
                overflow: 'hidden',
                bgcolor: '#f6ebe9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CiImageOn size={64} color="#d8aaa2" />
            </Box>
          )}

          <Typography
            variant="h5"
            my={2}
            sx={{ fontSize: '16px', color: theme => theme.palette.grey[600], fontWeight: 400 }}
          >
            Posts
          </Typography>

          {/* Create Post Input */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '15px',
              p: '15px',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Avatar
                src={getMediaUrl(profile?.avatar)}
                alt={profile?.name}
                sx={{ width: 30, height: 30, borderRadius: '22.5px' }}
              />
              {/* <Avatar sx={{ width: 30, height: 30, borderRadius: '15px' }} /> */}
              {!isExpanded ? (
                // Collapsed state - placeholder
                <Box
                  onClick={() => setIsExpanded(true)}
                  sx={{
                    flex: 1,
                    bgcolor: '#eee',
                    borderRadius: '12px',
                    px: '17px',
                    py: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Figtree, sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '19px',
                      color: '#b2b2b2',
                    }}
                  >
                    Start a Post..
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <ImageOutlinedIcon sx={{ fontSize: 20, color: '#333' }} />
                  </Box>
                </Box>
              ) : (
                // Expanded state - input form
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={handleCancel}>
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <RichTextEditor
                    value={postBody}
                    onChange={setPostBody}
                    onFetchMentionUsers={fetchMentionUsers}
                    placeholder="What's on your mind?"
                  />

                  {/* Image Previews */}
                  {images.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {images.map(img => (
                        <Box key={img.preview} sx={{ position: 'relative', width: 80, height: 80 }}>
                          <Box
                            component="img"
                            src={img.preview}
                            alt="Preview"
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              objectFit: 'cover',
                              opacity: img.uploading ? 0.5 : 1,
                            }}
                          />
                          {img.uploading && (
                            <CircularProgress
                              size={24}
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                              }}
                            />
                          )}
                          {!img.uploading && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveImage(img.preview)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'white',
                                boxShadow: 1,
                                '&:hover': { bgcolor: 'grey.100' },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleImageSelect}
                    />
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={images.length >= 10}
                    >
                      <ImageOutlinedIcon sx={{ fontSize: 24, color: images.length >= 10 ? '#ccc' : '#333' }} />
                    </IconButton>
                    <Button
                      variant="contained"
                      onClick={handlePost}
                      disabled={!canPost}
                      sx={{
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontFamily: 'Figtree, sans-serif',
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      {createPostMutation.isPending ? 'Posting...' : 'Post'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Posts List */}
          {posts.length === 0 && !isLoading ? (
            <Alert severity="info">No posts yet. Be the first to start a discussion!</Alert>
          ) : (
            <Stack spacing={2}>
              {posts.map(post => (
                <PostCard key={post.id} post={post} spaceId={parentSpace.id} />
              ))}

              {/* Load more sentinel */}
              <Box ref={loadMoreRef} sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                {isFetchingNextPage && <CircularProgress size={24} />}
                {!hasNextPage && posts.length > 0 && (
                  <Typography
                    sx={{
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 14,
                      color: '#808080',
                    }}
                  >
                    No more posts
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Box>
        <WhosOnline />
      </Box>
      {showTermsDialog && termsAcceptance?.terms && (
        <FeedTermsAcceptanceDialog spaceId={subSpace.id} terms={termsAcceptance.terms} />
      )}
    </>
  );
};
